import type { RunMetricOutput } from "@/types/RunMetricOutput"

type PanelConfidence = "high" | "medium" | "low" | null

export interface JudgeConfidence {
  status: "ok" | "unavailable"
  panelConfidence: PanelConfidence
  selectedAnswer: string
  selectedAnswerProbability: number | null
  distribution: { label: string; probability: number }[]
  mostLikelyAnswers: string[]
  alignment: "aligned" | "disagrees" | "inconclusive" | "unavailable"
  reviewRequired: boolean
  reason: string | null
}

export interface JudgeConfidenceOptions {
  binary?: boolean
  expectedOutputType?: "string" | "float"
}

/** A present but malformed contract must not fall back to a historical estimate. */
export function getJudgeConfidence(
  output: Pick<
    RunMetricOutput,
    "result" | "result_json" | "output_type" | "float_value" | "string_value"
  >,
  options?: JudgeConfidenceOptions,
): JudgeConfidence | null {
  const hasStructuredConfidence = Object.hasOwn(
    output.result ?? {},
    "judge_confidence",
  )
  if (
    !hasStructuredConfidence &&
    !Object.hasOwn(output.result_json ?? {}, "judge_confidence")
  ) {
    return null
  }
  const raw: unknown = hasStructuredConfidence
    ? output.result?.judge_confidence
    : output.result_json?.judge_confidence

  const invalid: JudgeConfidence = {
    status: "unavailable",
    panelConfidence: null,
    selectedAnswer: "",
    selectedAnswerProbability: null,
    distribution: [],
    mostLikelyAnswers: [],
    alignment: "unavailable",
    reviewRequired: false,
    reason: "invalid_payload",
  }
  if (!isRecord(raw)) {
    return invalid
  }
  const panelConfidence = raw.panel_confidence
  if (
    (panelConfidence !== null &&
      panelConfidence !== "high" &&
      panelConfidence !== "medium" &&
      panelConfidence !== "low") ||
    typeof raw.selected_answer !== "string" ||
    !raw.selected_answer ||
    (raw.reason !== null && typeof raw.reason !== "string")
  ) {
    return invalid
  }
  const matchesVerdict =
    output.output_type === "string"
      ? output.string_value === raw.selected_answer
      : output.output_type === "float" &&
        output.float_value !== null &&
        Number.isFinite(output.float_value) &&
        raw.selected_answer.trim() !== "" &&
        Number(raw.selected_answer) === output.float_value
  if (
    !matchesVerdict ||
    (options?.expectedOutputType !== undefined &&
      output.output_type !== options.expectedOutputType) ||
    (options?.binary && !isBinaryAnswer(raw.selected_answer))
  ) {
    return invalid
  }
  if (raw.status === "unavailable") {
    if (
      raw.alignment !== "unavailable" ||
      raw.review_required !== false ||
      raw.selected_answer_probability !== null ||
      !isRecord(raw.distribution) ||
      Object.keys(raw.distribution).length !== 0 ||
      !Array.isArray(raw.most_likely_answers) ||
      raw.most_likely_answers.length !== 0
    ) {
      return invalid
    }
    return {
      ...invalid,
      panelConfidence,
      selectedAnswer: raw.selected_answer,
      reason: raw.reason,
    }
  }
  if (raw.status !== "ok" || !isRecord(raw.distribution)) {
    return invalid
  }
  if (
    options?.binary &&
    raw.selected_answer === "UNKNOWN" &&
    raw.reason !== "unscoreable_evidence"
  ) {
    return invalid
  }

  const distribution = Object.entries(raw.distribution)
  if (
    distribution.length < 2 ||
    distribution.length > 16 ||
    !distribution.every(
      ([label, probability]) =>
        label &&
        (!options?.binary || isBinaryAnswer(label)) &&
        isProbability(probability),
    )
  ) {
    return invalid
  }
  const entries = distribution.map(([label, probability]) => ({
    label,
    probability: probability as number,
  }))
  if (
    Math.abs(entries.reduce((sum, entry) => sum + entry.probability, 0) - 1) >
    1e-6
  ) {
    return invalid
  }
  const selectedProbability =
    entries.find((entry) => entry.label === raw.selected_answer)?.probability ??
    null
  const maximum = Math.max(...entries.map((entry) => entry.probability))
  const mostLikely = entries
    .filter((entry) => Math.abs(entry.probability - maximum) <= 1e-12)
    .map((entry) => entry.label)
  const unscoreable =
    raw.selected_answer === "UNKNOWN" && raw.reason === "unscoreable_evidence"
  // Categorical UNKNOWN is a literal label, not a binary abstention.
  if (
    unscoreable &&
    options?.expectedOutputType === "string" &&
    !options.binary
  ) {
    return invalid
  }
  const alignment =
    unscoreable || selectedProbability === null || mostLikely.length !== 1
      ? "inconclusive"
      : mostLikely.includes(raw.selected_answer)
        ? "aligned"
        : "disagrees"
  const reason = unscoreable
    ? "unscoreable_evidence"
    : selectedProbability === null
      ? "answer_outside_distribution"
      : mostLikely.length !== 1
        ? "tied_probabilities"
        : null
  if (
    raw.reason !== reason ||
    raw.selected_answer_probability !== selectedProbability ||
    raw.alignment !== alignment ||
    raw.review_required !== (alignment !== "aligned") ||
    !Array.isArray(raw.most_likely_answers) ||
    raw.most_likely_answers.length !== mostLikely.length ||
    !mostLikely.every((answer) =>
      (raw.most_likely_answers as unknown[]).includes(answer),
    )
  ) {
    return invalid
  }
  const isNumeric = entries.every(
    (entry) =>
      entry.label.trim() !== "" && Number.isFinite(Number(entry.label)),
  )
  if (
    output.output_type === "float" &&
    (!isNumeric ||
      new Set(entries.map((entry) => Number(entry.label))).size !==
        entries.length)
  ) {
    return invalid
  }
  return {
    status: "ok",
    panelConfidence,
    selectedAnswer: raw.selected_answer,
    selectedAnswerProbability: selectedProbability,
    distribution:
      output.output_type === "float" && isNumeric
        ? entries.toSorted((a, b) => Number(a.label) - Number(b.label))
        : entries,
    mostLikelyAnswers: mostLikely,
    alignment,
    reviewRequired: raw.review_required,
    reason: raw.reason,
  }
}

function isBinaryAnswer(value: string): boolean {
  return value === "YES" || value === "NO" || value === "UNKNOWN"
}

function isProbability(value: unknown): value is number {
  return (
    typeof value === "number" &&
    Number.isFinite(value) &&
    value >= 0 &&
    value <= 1
  )
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}
